const keyOf = (el) => el?.dataset?.productHandle || '';
const productCardCache = new Map();
const productCardSampleCache = new Map();

const LIGHT_BACKGROUND_THRESHOLD = 0.48;
const HEART_REGION = { top: 17, right: 10, size: 27 };

const loadSampleImage = (url) => {
  if (!productCardSampleCache.has(url)) {
    productCardSampleCache.set(url, new Promise((resolve) => {
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.onload = () => resolve(sample);
      sample.onerror = () => resolve(null);
      sample.src = url;
    }));
  }

  return productCardSampleCache.get(url);
};

const sampleRegionLuminance = (sample, imageRect, targetRect) => {
  const { naturalWidth, naturalHeight } = sample;
  if (!naturalWidth || !naturalHeight) return null;

  const left = Math.max(targetRect.left, imageRect.left);
  const top = Math.max(targetRect.top, imageRect.top);
  const right = Math.min(targetRect.right, imageRect.right);
  const bottom = Math.min(targetRect.bottom, imageRect.bottom);
  if (right <= left || bottom <= top) return null;

  const scale = Math.max(imageRect.width / naturalWidth, imageRect.height / naturalHeight);
  const sourceWidth = imageRect.width / scale;
  const sourceHeight = imageRect.height / scale;
  const cropX = (naturalWidth - sourceWidth) / 2;
  const cropY = (naturalHeight - sourceHeight) / 2;
  const sourceX = cropX + ((left - imageRect.left) / imageRect.width) * sourceWidth;
  const sourceY = cropY + ((top - imageRect.top) / imageRect.height) * sourceHeight;
  const sampleWidth = ((right - left) / imageRect.width) * sourceWidth;
  const sampleHeight = ((bottom - top) / imageRect.height) * sourceHeight;

  const canvas = document.createElement('canvas');
  canvas.width = 12;
  canvas.height = 12;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  try {
    context.drawImage(sample, sourceX, sourceY, sampleWidth, sampleHeight, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let luminance = 0;
    let pixelCount = 0;

    for (let index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] === 0) continue;
      luminance += (0.2126 * pixels[index] + 0.7152 * pixels[index + 1] + 0.0722 * pixels[index + 2]) / 255;
      pixelCount += 1;
    }

    return pixelCount ? luminance / pixelCount : null;
  } catch (error) {
    return null;
  }
};

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this._ctrl = null;
    this._heartRevision = 0;
  }

  showHover() {
    if (window.innerWidth < 1024 || !this.hoverImageWrapper) return;
    this.container?.classList.add('is-hover');
  }

  hideHover() {
    if (window.innerWidth < 1024 || !this.hoverImageWrapper) return;
    this.container?.classList.remove('is-hover');
  }

  heartRegionRect(imageRect) {
    const right = imageRect.right - HEART_REGION.right;
    const top = imageRect.top + HEART_REGION.top;

    return { top, right, bottom: top + HEART_REGION.size, left: right - HEART_REGION.size };
  }

  async updateHeartContrast() {
    const image = this.imageWrapper?.querySelector('img');
    const url = image?.currentSrc || image?.src;
    if (!url || !image.complete || !image.naturalWidth) return;

    const imageRect = image.getBoundingClientRect();
    if (!imageRect.width || !imageRect.height) return;

    const revision = ++this._heartRevision;
    const sample = await loadSampleImage(url);
    if (!sample || revision !== this._heartRevision || !this.isConnected) return;

    const luminance = sampleRegionLuminance(sample, imageRect, this.heartRegionRect(imageRect));
    if (luminance === null) return;

    const backgroundIsLight = luminance >= LIGHT_BACKGROUND_THRESHOLD;
    this.classList.toggle('is-dark-on-image', backgroundIsLight);
    this.classList.toggle('is-light-on-image', !backgroundIsLight);
  }

  initHeartContrast() {
    const image = this.imageWrapper?.querySelector('img');
    if (!image) return;

    this.updateHeartContrast();
    image.addEventListener('load', () => this.updateHeartContrast());
  }

  applyTypography(targetRoot = this) {
    const d = this.wrapper?.dataset;
    if (!d) return;
    const classes = [];
    if (d.font) classes.push(`font-${d.font}`);
    if (d.fontSize) classes.push(`size-${d.fontSize}`);
    if (d.fontSizeMobile) classes.push(`small--size-${d.fontSizeMobile}`);
    if (d.fontTitleCase) classes.push(`text-${d.fontTitleCase}`);
    const target = (targetRoot.querySelector?.('[data-typography]')) || targetRoot;
    if (classes.length) target.classList.add(...classes);
  }

  applyInfoHeight() {
    if (window.innerWidth >= 1025) {
      const infoEl = this.querySelector('[data-product-info]');
      if (infoEl) {
        const height = infoEl.offsetHeight;
        infoEl.style.minHeight = `${height}px`;
      }
    }
  }

  markSelected(selectedKey) {
    if (!selectedKey) return;

    const swatches = [...this.querySelectorAll('.product-card-swatch')];
    swatches.forEach((s) => {
      const isSelected = keyOf(s) === selectedKey;
      s.classList.toggle('selected', isSelected);
      s.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    swatches
      .find((s) => keyOf(s) === selectedKey)
      ?.classList.remove('product-card-swatch--overflow');
  }

  connectedCallback() {
    this.wrapper = this.closest('[data-collection-handle],[data-font],[data-font-size],[data-font-size-mobile],[data-font-title-case]');
    this.container = this.querySelector('.product-card');

    this.imageWrapper = this.querySelector('[data-product-image]');
    this.hoverImageWrapper = this.querySelector('[data-product-hover]');

    this.init();
    this.applyTypography();
    this.applyInfoHeight();
    this.initHeartContrast();
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    this._swatchObserver?.disconnect();
    window.clearTimeout(this._swatchTimer);
    this._heartRevision += 1;
  }

  init() {
    if (!this.container) return;
    const onEnter = () => this.showHover();
    const onLeave = () => this.hideHover();
    this.container.addEventListener('mouseenter', onEnter);
    this.container.addEventListener('mouseleave', onLeave);

    const colorsRow = this.querySelector('.product-card-colors');
    if (colorsRow) {
      const swatchRow = colorsRow.querySelector('.product-card-swatches');

      if (swatchRow) {
        swatchRow.addEventListener('scroll', () => this.setSwatchEdges(), { passive: true });
        this._swatchObserver = new ResizeObserver(() => this.fitSwatchWindow());
        this._swatchObserver.observe(colorsRow);
      }

      if (swatchRow?.querySelector('.product-card-swatch--overflow')) {
        this.addEventListener('pointerover', (e) => {
          if (e.pointerType !== 'touch' && e.target.closest('.product-card-details')) this.openSwatchWindow();
        });

        this.addEventListener('pointerout', (e) => {
          const details = e.target.closest('.product-card-details');
          if (!details || details.contains(e.relatedTarget) || colorsRow.classList.contains('is-expanded')) return;
          this.closeSwatchWindow();
        });
      }

      colorsRow.addEventListener('click', (e) => {
        const expandButton = e.target.closest('[data-swatch-expand]');
        if (expandButton) {
          const expanded = colorsRow.classList.toggle('is-expanded');
          expandButton.setAttribute('aria-expanded', expanded);

          if (expanded) {
            this.openSwatchWindow();
          } else {
            this.closeSwatchWindow();
          }

          return;
        }

        const swatch = e.target.closest('.product-card-swatch');
        if (!swatch || !colorsRow.contains(swatch)) return;

        e.preventDefault();
        const handle = swatch.dataset.productHandle;
        if (!handle) return;

        if (colorsRow && swatch.classList.contains('product-card-swatch--overflow')) {
          colorsRow.classList.add('is-expanded');
          expandButton?.setAttribute('aria-expanded', 'true');
        }

        this.markSelected(keyOf(swatch));

        this.updateCard(handle, keyOf(swatch));
      });
    }
  }

  openSwatchWindow() {
    const row = this.querySelector('.product-card-swatches');
    if (!row || row.classList.contains('state--open') || !window.matchMedia('(min-width: 768px)').matches) return;

    window.clearTimeout(this._swatchTimer);
    this._swatchTimer = null;
    row.classList.add('state--open');
    this.fitSwatchWindow();
  }

  closeSwatchWindow() {
    const row = this.querySelector('.product-card-swatches');
    if (!row || !row.classList.contains('state--open')) return;

    row.classList.remove('state--open');
    row.scrollTo({ left: 0, behavior: 'smooth' });

    window.clearTimeout(this._swatchTimer);
    this._swatchTimer = window.setTimeout(() => {
      this._swatchTimer = null;
      this.fitSwatchWindow();
    }, 300);
  }

  fitSwatchWindow() {
    const colors = this.querySelector('.product-card-colors');
    const row = colors?.querySelector('.product-card-swatches');
    if (!row || this._swatchTimer) return;

    row.classList.remove('state--overflowing', 'state--start', 'state--end');
    row.style.removeProperty('max-width');
    if (!window.matchMedia('(min-width: 768px)').matches) return;

    const open = row.classList.contains('state--open');
    const style = getComputedStyle(row);
    const gap = parseFloat(style.columnGap) || 0;
    const fullSize = parseFloat(style.getPropertyValue('--swatch-size')) || 0;
    const lightSize = parseFloat(style.getPropertyValue('--swatch-size-light')) || fullSize;
    const colorsGap = parseFloat(getComputedStyle(colors).columnGap) || 0;
    const reserved = [...colors.children]
      .filter((el) => el !== row && el.getClientRects().length && !(open && el.matches('.product-card-colors-toggle')))
      .reduce((total, el) => total + el.scrollWidth + colorsGap, 0);
    const available = colors.clientWidth - reserved;

    const swatches = [...row.querySelectorAll('.product-card-swatch')]
      .filter((swatch) => open || !swatch.classList.contains('product-card-swatch--overflow'))
      .map((swatch, index) => ({ swatch, index, order: parseInt(getComputedStyle(swatch).order, 10) || 0 }))
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .map(({ swatch }) => swatch);

    let start = parseFloat(style.paddingLeft) || 0;
    let edge = null;
    let overflows = false;

    for (const swatch of swatches) {
      const size = swatch.matches('.light-swatch:not(.selected)') ? lightSize : fullSize;

      if (start + size > available) {
        edge = start + size / 2 <= available ? start + size / 2 : edge;
        overflows = true;
        break;
      }

      edge = start + size / 2;
      start += size + gap;
    }

    if (!overflows || edge === null) return;

    row.style.maxWidth = `${edge}px`;
    row.classList.add('state--overflowing');
    row.classList.toggle('state--start', row.scrollLeft <= 1);
  }

  setSwatchEdges() {
    const row = this.querySelector('.product-card-swatches');
    if (!row?.classList.contains('state--overflowing')) return;

    const maximum = row.scrollWidth - row.clientWidth;
    row.classList.toggle('state--start', row.scrollLeft <= 1);
    row.classList.toggle('state--end', row.scrollLeft >= maximum - 1);
  }

  async updateCard(handle, selectedKey = null) {
    try {
      if (this._ctrl) this._ctrl.abort();
      this._ctrl = new AbortController();

      const collectionHandle = this.wrapper?.dataset?.collectionHandle || null;
      const url = collectionHandle
        ? `/collections/${encodeURIComponent(collectionHandle)}/products/${encodeURIComponent(handle)}?section_id=product-card`
        : `/products/${encodeURIComponent(handle)}?section_id=product-card`;

      let html = productCardCache.get(url);
      if (!html) {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: this._ctrl.signal });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

        html = await res.text();
        productCardCache.set(url, html);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshRoot = doc.querySelector('product-card');
      if (!freshRoot) throw new Error('No product-card in section response');

      const freshMedia = freshRoot.querySelector('.product-card-media');
      const liveMedia = this.querySelector('.product-card-media');
      if (freshMedia && liveMedia) liveMedia.replaceWith(freshMedia);

      const freshQuickAdd = freshRoot.querySelector('quick-add');
      const liveQuickAdd = this.querySelector('quick-add');
      const liveColors = this.querySelector('.color-picker');

      if (freshQuickAdd && liveQuickAdd) {
        liveQuickAdd.replaceWith(freshQuickAdd);

        const freshColors = freshQuickAdd.querySelector('.color-picker');
        if (liveColors && freshColors) freshColors.replaceWith(liveColors);
      }

      this.imageWrapper = this.querySelector('[data-product-image]');
      this.hoverImageWrapper = this.querySelector('[data-product-hover]');

      this.markSelected(selectedKey);
      this.fitSwatchWindow();
      this.initHeartContrast();

      if (typeof alignProductCardsHeight === 'function') {
        requestAnimationFrame(() => alignProductCardsHeight());
      }
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('updateCard fetch/replace error', e);
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
